<script setup lang="ts">
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import {onMounted, onUnmounted, ref, watch} from "vue";
import {Info} from "@lucide/vue";
import {SearchItem} from "@/auto-complete-searchbar/interfaces";
import AudienceFilters from "@/communication/components/audience/AudienceFilters.vue";
import {massCertificationApi} from "@/container";
import SearchBarService from "@/core/services/SearchBarService";
import axios from "axios";
import {MassCertificationCustomer} from "@/core/interfaces/MassCertification";
import {MassCertificationMetaData} from "@/core/http/resources/MassCertificationResource";
import MassCertificationService from "@/communication/services/MassCertificationService";
import debounce from "lodash.debounce";
import CustomerMessageList from "@/communication/components/customer-list/CustomerMessageList.vue";
import EmptyCustomerMessageList from "@/communication/components/customer-list/EmptyCustomerMessageList.vue";

const props = defineProps<{
    mode: 'sms' | 'notification';
}>();

const emits = defineEmits<{
    'loading': [boolean];
    'selected': [];
}>();

const filtersRef = ref<InstanceType<typeof AudienceFilters> | null>(null);

const filters = ref<SearchItem[]>([]);


const selected = defineModel<MassCertificationCustomer[]>("selected", {default: []});
const customers = ref<MassCertificationCustomer[]>([]);
const meta = ref<MassCertificationMetaData>();
const loading = ref<boolean>(false);
const abortController = ref<AbortController | null>(null);
const page = ref<number>(1);
const limit = ref<number>(20);

onMounted(() => {
    search();
});

onUnmounted(() => {
    if (abortController.value) {
        abortController.value.abort();
    }
});

watch(() => props.mode, () => {
    page.value = 1;
    debouncedSearch();
});

watch(() => filters, () => {
    page.value = 1;
    debouncedSearch();
}, {deep: true});

const onTableOptions = (options: {page: number; itemsPerPage: number}) => {
    page.value = options.page;
    limit.value = options.itemsPerPage;
    debouncedSearch();
};

const search = async () => {
    if (abortController.value) {
        abortController.value.abort();
    }

    let cancelled = false;

    abortController.value = new AbortController();
    loading.value = true;
    emits('loading', true);

    const defaultParams = props.mode === 'notification'
        ? {query: {onboarded: [true]}, useDefaultFilters: false}
        : {};

    return massCertificationApi().customers({
        page: page.value,
        limit: limit.value,
        ...defaultParams,
        query: {...defaultParams.query, ...SearchBarService.transformToQueryParams(filters.value)},
    }, abortController.value)
        .then((response) => {
            meta.value = response.meta;
            customers.value = MassCertificationService.mapApiResponse(response.data);
        })
        .catch((error) => {
            if (axios.isCancel(error)) {
                cancelled = true;
            }
        })
        .finally(() => {
            if (!cancelled) {
                loading.value = false;
                emits('loading', false);
                cancelled = false;
            }
            abortController.value = null;
        });
};

const debouncedSearch = debounce(search, 300);
</script>

<template>
  <BaseCard>
    <BaseCardBody class="flex flex-col gap-3">
      <AudienceFilters ref="filtersRef" v-model:filters="filters" :mode="mode" />
      <div class="bg-muted rounded-lg p-4 flex items-center gap-1.5">
        <Info class="text-primary shrink-0 size-5" />
        <span v-if="mode === 'sms'" class="leading-tight text-muted-foreground">
          La liste ne contient que les patients avec un <span class="font-medium text-black">numéro de mobile valide (06/07)</span>.</span>
        <span v-else class="leading-tight text-muted-foreground">
          Seuls les patients ayant <span class="font-medium text-black">installé l'application Apodis</span> recevront la notification.
        </span>
      </div>
      <EmptyCustomerMessageList
        v-if="!loading && !customers.length" class="mt-4"
        @filters="() => filtersRef?.showFilters()"
      />
      <CustomerMessageList
        v-else
        :mode="mode"
        :is-loading="loading"
        :customers="customers"
        :total="meta?.total ?? 0"
        :page="page"
        :limit="limit"
        @update:options="onTableOptions"
        @update:selection="selected = $event"
      />
    </BaseCardBody>
  </BaseCard>
</template>

<style scoped>

</style>
